import React, { useState } from 'react';

import { Empty, Flex } from 'antd-v5';
import { FormattedMessage } from 'react-intl';
import { Button, Search, Text } from 'scouting-ui/lib';

import { intl } from '@shared/localization';

import { SEARCHABLE_ROSTER_MIN } from '../constants';
import type { ClassYouth } from '../types';
import styles from './ClassEntry.module.scss';
import YouthOption from './YouthOption';

interface YouthPickerProps {
  value?: number[];
  onChange?: (memberIds: number[]) => void;
  roster: ClassYouth[];
  disabledReasons: Map<number, string>;
  eligibleCount: number;
  disabled: boolean;
}

export default function YouthPicker(props: YouthPickerProps) {
  const {
    value = [],
    onChange,
    roster,
    disabledReasons,
    eligibleCount,
    disabled,
  } = props;

  const [search, setSearch] = useState('');

  const selected = new Set(value);
  const searchable = roster.length >= SEARCHABLE_ROSTER_MIN;
  const query = searchable ? search.trim().toLowerCase() : '';
  const shown = query
    ? roster.filter(youth => youth.name.toLowerCase().includes(query))
    : roster;
  const shownIds = new Set(
    shown
      .map(youth => youth.memberId)
      .filter(memberId => !disabledReasons.has(memberId)),
  );
  const allShownOn =
    shownIds.size > 0 && [...shownIds].every(id => selected.has(id));

  const toggleYouth = (memberId: number) =>
    onChange?.(
      selected.has(memberId)
        ? value.filter(id => id !== memberId)
        : [...value, memberId],
    );

  const toggleAllShown = () =>
    onChange?.(
      allShownOn
        ? value.filter(id => !shownIds.has(id))
        : [...new Set([...value, ...shownIds])],
    );

  return (
    <>
      <Flex wrap align="center" gap="10px 14px">
        <Text font="body-overline" color="color-text-medium">
          <FormattedMessage id="page.mbcClassEntry.approveFor" />
        </Text>
        {searchable && (
          <div>
            <Search
              placeholder={intl.formatMessage({
                id: 'page.mbcClassEntry.searchYouth',
              })}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        )}
        <Text
          font="body-caption"
          color="color-text-medium"
          className={styles.selectedCount}
        >
          <FormattedMessage
            id="page.mbcClassEntry.selectedCount"
            values={{ selected: value.length, total: eligibleCount }}
          />
        </Text>
        <Button
          size="small"
          type="default"
          disabled={disabled || shownIds.size === 0}
          onClick={toggleAllShown}
        >
          <FormattedMessage
            id={allShownOn ? 'shared.clearAll' : 'shared.selectAll'}
          />
        </Button>
      </Flex>

      {shown.length > 0 ? (
        <div role="group" className={styles.youthGrid}>
          {shown.map(youth => (
            <YouthOption
              key={youth.memberId}
              youth={youth}
              checked={selected.has(youth.memberId)}
              disabled={disabled}
              disabledReason={disabledReasons.get(youth.memberId)}
              onToggle={toggleYouth}
            />
          ))}
        </div>
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <FormattedMessage
              id={
                roster.length === 0
                  ? 'page.mbcClassEntry.noYouthInClass'
                  : 'page.mbcClassEntry.noYouthMatch'
              }
            />
          }
        />
      )}
    </>
  );
}
